// O PDF que a família entrega na mão do médico.
//
// Antes isto era a impressão do navegador. Funcionava, mas obrigava a pessoa a
// atravessar o diálogo de imprimir — escolher destino, mexer em margem — e o
// resultado dependia dessas escolhas. Agora o arquivo é montado aqui e baixa
// pronto: um clique, um PDF, sempre igual.
//
// A forma segue o que um documento clínico brasileiro tem: uma faixa de
// identificação com o paciente e o registro, seções numeradas, e o fecho com a
// natureza do documento. Não é imitação de prontuário — é o contrário: o
// documento diz em toda página que NÃO é prontuário. Mas a organização da
// informação é a que o médico já sabe percorrer, e é isso que faz ele ler em
// vez de devolver.
//
// pdfmake e as fontes entram por `import()` dentro da função: são quase 2 MB
// que ninguém precisa baixar para ver o monitor do quarto.
import {
  ROTULO_GRUPO, formatarData, formatarDataHora, formatarDiaMes, formatarNumero,
  PERGUNTAS_DO_DIA, ROTULO_RESPOSTA, sonoEmTexto,
} from '@medicov1/shared';
import type { RelatorioDTO, Periodo, Versao } from './relatorioTipos';
import { faixaDoSinal, geometriaDoSinal, sinaisComGrafico } from './graficoDeSinais';
import { identificar } from './identificacao';
import { notaNoPdf } from './notaNoPdf';

// Tinta de documento: preto de verdade só no que estrutura, cinza no resto.
export const TINTA = '#111111';
export const MEIO = '#3f3f46';
export const FRACO = '#71717a';
export const LINHA = '#d4d4d8';
const REGUA = '#18181b';
export const ALTO = '#9f1239';
const BAIXO = '#155e75';
export const OK = '#15803d';
export const ATENCAO = '#9a3412';
export const FUNDO_CAIXA = '#f4f6f4';
const ZEBRA = '#fafafa';

const ROTULO_SINAL: Record<string, string> = {
  glicemia: 'Glicemia', pa_sistolica: 'Pressão sistólica', pa_diastolica: 'Pressão diastólica',
  frequencia_cardiaca: 'Frequência cardíaca', saturacao: 'Saturação de oxigênio',
  temperatura: 'Temperatura', frequencia_respiratoria: 'Frequência respiratória',
  diurese: 'Diurese', evacuacoes: 'Evacuações', dieta_enteral: 'Dieta enteral',
  balanco_hidrico: 'Balanço hídrico', peso: 'Peso',
};

const ROTULO_PERIODO: Record<Periodo, string> = {
  turno: 'Passagem de turno',
  diario: 'Diário', semanal: 'Semanal', quinzenal: 'Quinzenal', geral: 'Geral',
};

const num = (n: number) => formatarNumero(n, 1);
const faixa = (n: number) => formatarNumero(n, 2);

/** Cor do valor fora da faixa. Sinalização, não diagnóstico. */
const corDaSituacao = (s: string) => (s === 'alto' ? ALTO : s === 'baixo' ? BAIXO : TINTA);

/**
 * Uma linha da tabela de laboratório.
 *
 * O valor fora da faixa sai marcado com a PALAVRA — "alto", "baixo" — e não só
 * com a cor. O motivo é concreto: este PDF vai ser impresso, e a impressora de
 * uma casa ou de um posto de enfermagem imprime em preto e branco. Com a cor
 * como único sinal, a legenda diria "a cor indica" num papel sem cor nenhuma, e
 * a informação simplesmente desapareceria.
 */
function linhaDeLab(l: {
  rotulo: string; grupo: string; valorTexto: string; unidade: string;
  situacao: string; anteriorTexto: string | null;
  direcao: string | null; referencia: { min: number; max: number } | null;
  coletadoEm: string;
}): unknown[] {
  const fora = l.situacao === 'alto' || l.situacao === 'baixo';
  return [
    {
      stack: [
        { text: l.rotulo, bold: true },
        { text: (ROTULO_GRUPO[l.grupo as keyof typeof ROTULO_GRUPO] ?? '').toUpperCase(), fontSize: 5.8, color: FRACO, characterSpacing: 0.5 },
      ],
    },
    {
      text: [
        { text: l.valorTexto, bold: true, fontSize: 9, color: corDaSituacao(l.situacao) },
        { text: `  ${l.unidade}`, fontSize: 6.4, color: FRACO },
        ...(fora
          ? [{ text: `  ${l.situacao.toUpperCase()}`, fontSize: 5.8, bold: true, color: corDaSituacao(l.situacao), characterSpacing: 0.5 }]
          : []),
      ],
    },
    { text: l.anteriorTexto ?? '—', color: FRACO },
    // Sem seta: a Roboto embutida no PDF não tem o glifo, e saía um "I" no
    // lugar da flecha. A palavra sozinha diz a mesma coisa.
    { text: l.direcao ?? '—', color: l.direcao === 'estavel' ? FRACO : MEIO },
    { text: l.referencia ? `${faixa(l.referencia.min)}–${faixa(l.referencia.max)}` : '—', color: FRACO },
    // INSTANTE, não dia puro: sem o `new Date`, a coleta das 22h sai no papel
    // com a data do dia seguinte — e o papel é o que o médico lê.
    { text: formatarData(new Date(l.coletadoEm)), color: FRACO },
  ];
}

/* -------------------------------------------------------------- tijolos */

/** Título de seção: numerado, em caixa alta, com régua embaixo. */
export function titulo(n: number, texto: string, complemento?: string) {
  return {
    // `headlineLevel` mantém o título grudado no que ele anuncia: título só no
    // pé da página é a coisa que mais denuncia documento gerado às pressas.
    headlineLevel: 'secao',
    margin: [0, 14, 0, 6] as [number, number, number, number],
    table: {
      widths: ['*'],
      body: [[{
        text: [
          { text: `${n}. `, color: FRACO },
          { text: texto.toUpperCase(), bold: true, characterSpacing: 1.1 },
          ...(complemento ? [{ text: `   ${complemento}`, color: FRACO, bold: false, characterSpacing: 0, fontSize: 7.5 }] : []),
        ],
        fontSize: 8.5, color: TINTA, margin: [0, 0, 0, 3] as [number, number, number, number],
      }]],
    },
    layout: {
      hLineWidth: (i: number) => (i === 1 ? 1 : 0),
      vLineWidth: () => 0,
      hLineColor: () => REGUA,
      paddingLeft: () => 0, paddingRight: () => 0,
      paddingTop: () => 0, paddingBottom: () => 0,
    },
  };
}

/** Traço fino entre linhas, cabeçalho com peso, e zebra para o olho não pular. */
const layoutTabela = {
  hLineWidth: (i: number, node: { table: { body: unknown[] } }) =>
    i === 0 ? 0 : i === 1 ? 0.9 : i === node.table.body.length ? 0.6 : 0.4,
  vLineWidth: () => 0,
  hLineColor: (i: number) => (i === 1 ? '#a1a1aa' : LINHA),
  fillColor: (i: number) => (i === 0 ? null : i % 2 === 0 ? ZEBRA : null),
  paddingLeft: () => 0,
  paddingRight: () => 8,
  paddingTop: () => 4.5,
  paddingBottom: () => 4.5,
};

const cabecalhoDeTabela = (textos: string[]) =>
  textos.map((t, i) => ({
    text: t.toUpperCase(), bold: true, fontSize: 6.4, color: TINTA,
    characterSpacing: 0.9, alignment: i === 0 ? 'left' : 'left',
  }));

/** Uma tabela de seção, com o cabeçalho repetido quando ela vira a página. */
function tabela(widths: (string | number)[], cabecalho: string[], linhas: unknown[][]) {
  return {
    table: { headerRows: 1, dontBreakRows: true, widths, body: [cabecalhoDeTabela(cabecalho), ...linhas] },
    layout: layoutTabela,
    fontSize: 8,
  };
}

/**
 * Título colado no que ele anuncia.
 *
 * "2. PARA PERGUNTAR NA VISITA" saiu na última linha da folha 1 e as perguntas
 * na folha 2: um título anunciando algo que não estava ali, pendurado embaixo
 * de um texto com que não tem relação, e um palmo de branco embaixo.
 *
 * O caminho óbvio seria `pageBreakBefore` do pdfmake — mas na versão 0.3 ele é
 * chamado só com o primeiro argumento, e `followingNodesOnPage` chega
 * `undefined`. A condição nunca valia, e pior: PARECIA funcionar, porque o PDF
 * saía igual sem erro nenhum.
 *
 * Então nada de heurística: o título e o primeiro pedaço do conteúdo viram um
 * nó indivisível. Ou os dois cabem nesta página, ou os dois vão para a próxima.
 * Só o que vem depois é que pode quebrar.
 */
export function comTitulo(titulo: unknown, primeiro: unknown, resto: unknown[] = []): unknown[] {
  return [{ unbreakable: true, stack: [titulo, primeiro] }, ...resto];
}

/** Rótulo miúdo por cima do valor: o jeito de ficha clínica de dizer um campo. */
const campo = (rotulo: string, valor: string) => ({
  stack: [
    { text: rotulo.toUpperCase(), fontSize: 5.8, color: FRACO, characterSpacing: 0.8, bold: true },
    { text: valor, fontSize: 9, color: TINTA, margin: [0, 1.5, 0, 0] as [number, number, number, number] },
  ],
});

/* ------------------------------------------------------------ documento */

/**
 * O documento inteiro, como estrutura — sem navegador nenhum.
 *
 * Separado do download de propósito: assim dá para gerar este mesmo PDF fora do
 * navegador e OLHAR o resultado antes de publicar. Um documento que a família
 * entrega na mão do médico não se confere por dedução.
 */
export function montarDocumento(
  dados: RelatorioDTO,
  periodo: Periodo,
  versao: Versao,
): Record<string, unknown> {
  const p = dados.paciente;
  const paraFamilia = versao === 'familia';
  // Versão do médico: identifica como o hospital, sem o apelido (27/09).
  const nomeNoTopo = identificar(p, !paraFamilia).nome;
  const conteudo: unknown[] = [];
  let n = 0;

  // ------------------------------------------------------- identificação
  conteudo.push({
    columns: [
      {
        width: '*',
        stack: [
          { text: nomeNoTopo, fontSize: 19, color: TINTA, margin: [0, 0, 0, 1] },
        ],
      },
      {
        width: 'auto',
        stack: [
          { text: `RELATÓRIO ${ROTULO_PERIODO[periodo].toUpperCase()}`, fontSize: 9, bold: true, characterSpacing: 1.4, alignment: 'right', color: TINTA },
          { text: dados.gerado.periodoRotulo, fontSize: 7.5, color: FRACO, alignment: 'right', margin: [0, 2, 0, 0] },
          { text: `emitido em ${formatarDataHora(dados.gerado.em)}`, fontSize: 7.5, color: FRACO, alignment: 'right' },
        ],
      },
    ],
  });

  // A faixa de campos: é o que o médico lê primeiro para saber de quem é o papel.
  const campos = [
    ...(!paraFamilia && p.nascimentoEm
      ? [campo('nascimento', `${formatarData(p.nascimentoEm)} · ${p.idade} anos`)]
      : [campo('idade', `${p.idade} anos`)]),
    ...(p.quarto ? [campo('leito', p.quarto)] : []),
    ...(!paraFamilia && p.registro ? [campo('prontuário', p.registro)] : []),
    ...(!paraFamilia && p.atendimento ? [campo('atendimento', p.atendimento)] : []),
    campo('internação', `${p.diasInternada}º dia · desde ${formatarData(p.internadoDesde)}`),
    ...(p.noSetorDesde && p.diasNoSetor !== null
      ? [campo('neste setor', `desde ${formatarData(p.noSetorDesde)} · ${p.diasNoSetor} ${p.diasNoSetor === 1 ? 'dia' : 'dias'}`)]
      : []),
  ];
  conteudo.push({
    margin: [0, 10, 0, 0],
    table: { widths: campos.map(() => '*'), body: [campos] },
    layout: {
      hLineWidth: (i: number) => (i === 0 ? 1.4 : i === 1 ? 0.6 : 0),
      vLineWidth: () => 0,
      hLineColor: (i: number) => (i === 0 ? REGUA : LINHA),
      paddingLeft: () => 0, paddingRight: () => 10,
      paddingTop: () => 7, paddingBottom: () => 7,
    },
  });

  conteudo.push({
    margin: [0, 10, 0, 0],
    table: {
      widths: ['*'],
      body: [[{
        text: [
          { text: 'Registro de acompanhamento feito pela família. ', color: MEIO },
          { text: 'Não é o prontuário do hospital e não substitui a avaliação da equipe assistente.', bold: true, color: TINTA },
          ...(paraFamilia ? [{ text: ' As explicações dizem o que cada medida é, em geral.', color: MEIO }] : []),
        ],
        fontSize: 7.8, lineHeight: 1.25, margin: [8, 5, 8, 5],
      }]],
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: (i: number) => (i === 0 ? 2.2 : 0),
      vLineColor: () => ATENCAO,
      paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
    },
  });

  // ------------------------------------------------------------- alerta
  // A primeira coisa depois da identificação, com moldura pesada e vermelha: é
  // o único bloco do documento que existe para ser lido por quem NÃO vai ler o
  // documento. Médico que entra no quarto, olha o papel por três segundos e
  // precisa sair sabendo que ela tem marca-passo.
  for (const a of dados.resumo.alertas) {
    // Vermelho só para o crítico. Se tudo grita, nada é ouvido.
    const cor = a.gravidade === 'critico' ? ALTO : ATENCAO;
    const fundo = a.gravidade === 'critico' ? '#fdf5f6' : '#fdf8f3';
    conteudo.push({
      unbreakable: true,
      margin: [0, 12, 0, 0],
      table: {
        widths: ['*'],
        body: [[{
          margin: [10, 8, 10, 8],
          stack: [
            { text: a.texto, fontSize: a.gravidade === 'critico' ? 9.5 : 8.6, bold: true, color: cor, characterSpacing: 0.4, lineHeight: 1.2 },
            { text: a.dispositivo.toUpperCase(), fontSize: 6.2, color: FRACO, bold: true, characterSpacing: 0.8, margin: [0, 2, 0, 0] },
            ...(a.restricoes.length > 0
              ? [{
                  margin: [0, 5, 0, 0],
                  table: {
                    widths: [116, '*'],
                    body: a.restricoes.map((r) => [
                      { text: r.o, fontSize: 7.6, bold: true, color: TINTA },
                      { text: r.porque, fontSize: 7.6, color: MEIO, lineHeight: 1.25 },
                    ]),
                  },
                  layout: {
                    hLineWidth: (i: number, node: { table: { body: unknown[] } }) =>
                      (i === 0 || i === node.table.body.length ? 0 : 0.4),
                    vLineWidth: () => 0, hLineColor: () => '#e4cdd3',
                    paddingLeft: () => 0, paddingRight: () => 8,
                    paddingTop: () => 3.5, paddingBottom: () => 3.5,
                  },
                }]
              : []),
            ...(a.liberado
              ? [{
                  margin: [0, 6, 0, 0],
                  table: {
                    widths: ['*'],
                    body: [[{ text: a.liberado, fontSize: 7.4, color: MEIO, margin: [7, 0, 0, 0], lineHeight: 1.25 }]],
                  },
                  layout: {
                    hLineWidth: () => 0, vLineWidth: (c: number) => (c === 0 ? 1.6 : 0),
                    vLineColor: () => OK,
                    paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
                  },
                }]
              : []),
          ],
        }]],
      },
      layout: {
        hLineWidth: () => (a.gravidade === 'critico' ? 1.4 : 0.7),
        vLineWidth: () => (a.gravidade === 'critico' ? 1.4 : 0.7),
        hLineColor: () => cor, vLineColor: () => cor,
        fillColor: () => fundo,
        paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
      },
    });
  }

  // ----------------------------------------------------- passagem de turno
  //
  // Quem entra no quarto precisa saber de quem está recebendo e desde quando —
  // para poder perguntar o que não estiver escrito, antes de a pessoa ir embora.
  if (dados.gerado.turnoQueSai) {
    const t = dados.gerado.turnoQueSai;
    conteudo.push({
      margin: [0, 0, 0, 10],
      table: {
        widths: ['*'],
        body: [[{
          stack: [
            {
              text: 'PASSANDO O TURNO', fontSize: 7, bold: true,
              characterSpacing: 1.1, color: OK, margin: [0, 0, 0, 4],
            },
            {
              text: `Pela família: ${t.familia.join(' e ') || 'não registrado'} — `
                + `desde ${formatarDataHora(t.inicioEm)} (${t.horas}h de turno).`,
              fontSize: 9, color: TINTA, lineHeight: 1.35,
            },
            ...(t.equipe.length > 0
              ? [{
                  text: `Equipe do hospital no turno: ${t.equipe.join(' · ')}.`,
                  fontSize: 8.4, color: MEIO, lineHeight: 1.35,
                  margin: [0, 2, 0, 0] as [number, number, number, number],
                }]
              : []),
            {
              text: 'O que está abaixo aconteceu neste turno. O que não estiver escrito aqui, '
                + 'pergunte a quem está saindo antes de ele ir embora.',
              fontSize: 7.4, color: FRACO, margin: [0, 3, 0, 0], lineHeight: 1.35,
            },
          ],
          margin: [10, 8, 10, 8],
        }]],
      },
      layout: {
        hLineWidth: () => 0.6, vLineWidth: () => 0.6,
        hLineColor: () => OK, vLineColor: () => OK,
        paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
      },
    });
  }

  // ------------------------------------------------------ resumo do caso
  if (dados.resumo.texto || dados.resumo.emUso.length > 0) {
    // O parágrafo do resumo é longo e pode quebrar entre páginas — mas as três
    // primeiras linhas vêm junto com o título, que é o que impede o órfão.
    conteudo.push(...comTitulo(
      titulo(++n, 'Resumo do caso'),
      dados.resumo.texto
        ? { text: dados.resumo.texto, fontSize: 9, color: TINTA, lineHeight: 1.4, margin: [0, 2, 0, 0] }
        : { text: '' },
    ));
    // O resumo ficou para trás: o documento diz o que aconteceu depois dele.
    const velho = dados.resumo.desatualizado;
    if (velho) {
      conteudo.push({
        // Inteira na mesma página: partida, a lista do que mudou perdia o fim.
        unbreakable: true,
        margin: [0, 6, 0, 2],
        table: {
          widths: ['*'],
          body: [[{
            margin: [8, 5, 8, 5],
            stack: [
              {
                text: [
                  { text: 'ESTE RESUMO PRECISA SER REVISTO.  ', bold: true, color: ATENCAO, fontSize: 7, characterSpacing: 0.6 },
                  { text: velho.escritoEm ? `Foi escrito em ${formatarData(velho.escritoEm)}. Depois disso:` : 'Motivo:', color: MEIO },
                ],
                fontSize: 8,
              },
              {
                ul: [...velho.itens, ...(velho.maisN > 0 ? [`e mais ${velho.maisN}`] : [])],
                fontSize: 7.8, color: MEIO, lineHeight: 1.25, margin: [0, 2, 0, 0],
              },
            ],
          }]],
        },
        layout: {
          hLineWidth: () => 0, vLineWidth: (c: number) => (c === 0 ? 2 : 0),
          vLineColor: () => ATENCAO, fillColor: () => FUNDO_CAIXA,
          paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
        },
      });
    } else if (dados.resumo.atualizadoEm) {
      conteudo.push({ text: `Resumo escrito em ${formatarData(new Date(dados.resumo.atualizadoEm))}.`, fontSize: 7, color: FRACO, margin: [0, 3, 0, 0] });
    }
    if (dados.resumo.emUso.length > 0) {
      conteudo.push({
        margin: [0, 6, 0, 0],
        text: [
          { text: 'Instalado agora: ', bold: true, color: TINTA },
          {
            text: dados.resumo.emUso
              .map((d) => `${d.nome}${d.local ? ` (${d.local})` : ''} desde ${formatarData(d.desde)}`)
              .join('; ') + '.',
            color: MEIO,
          },
        ],
        fontSize: 8, lineHeight: 1.3,
      });
    }
    if (dados.resumo.medicacoes.length > 0) {
      conteudo.push({
        margin: [0, 3, 0, 0],
        text: [
          { text: 'Em uso agora: ', bold: true, color: TINTA },
          {
            text: dados.resumo.medicacoes
              .map((m) => `${m.nome}${m.dose ? ` ${m.dose}` : ''}${m.via ? ` ${m.via}` : ''}`)
              .join('; ') + '.',
            color: MEIO,
          },
        ],
        fontSize: 8, lineHeight: 1.3,
      });
    }
  }

  // ------------------------------------------------------- sinais vitais
  if (dados.sinais.length > 0) {
    conteudo.push(titulo(++n, 'Sinais vitais', dados.gerado.periodoRotulo));
    conteudo.push(tabela(
      ['*', 48, 42, 42, 42, 46],
      ['Medida', 'Último', 'Menor', 'Maior', 'Média', 'Aferições'],
      dados.sinais.map((s) => [
        { text: [{ text: ROTULO_SINAL[s.tipo] ?? s.tipo, bold: true }, { text: `  ${s.unidade}`, fontSize: 6.4, color: FRACO }] },
        { text: num(s.ultimo), bold: true, fontSize: 9 },
        { text: num(s.menor), color: MEIO },
        { text: num(s.maior), color: MEIO },
        { text: num(s.media), color: MEIO },
        { text: String(s.quantas), color: FRACO },
      ]),
    ));

    // O gráfico dia a dia: a tabela diz o menor e o maior do período inteiro;
    // o gráfico mostra QUANDO — se a pressão baixa foi um dia ou a semana toda.
    const comGrafico = sinaisComGrafico(dados.sinais);
    if (comGrafico.length > 0) {
      const L = 300;
      const H = 34;
      const linhas = comGrafico.map((s) => {
        const dias = s.porDia!;
        const faixaUsual = faixaDoSinal(s.tipo);
        const g = geometriaDoSinal(dias, faixaUsual, L, H);
        const formas: unknown[] = [];
        if (g.faixa) formas.push({ type: 'rect', x: 0, y: g.faixa.y, w: L, h: g.faixa.h, color: '#e3f1e6' });
        for (const b of g.barras) formas.push({ type: 'line', x1: b.x, y1: b.y1, x2: b.x, y2: b.y2 + 0.01, lineWidth: 1.4, lineColor: '#a1a1aa' });
        if (g.pontos.length > 1) formas.push({ type: 'polyline', points: g.pontos, lineWidth: 0.8, lineColor: '#52525b' });
        for (const p of g.pontos) formas.push({ type: 'ellipse', x: p.x, y: p.y, r1: 1.4, r2: 1.4, color: TINTA });
        const ultimo = dias[dias.length - 1]!;
        return [
          {
            stack: [
              { text: ROTULO_SINAL[s.tipo] ?? s.tipo, bold: true, fontSize: 7.8 },
              ...(faixaUsual ? [{ text: `faixa usual ${num(faixaUsual.min)}–${num(faixaUsual.max)} ${s.unidade}`, fontSize: 6, color: FRACO }] : []),
            ],
          },
          {
            stack: [
              { canvas: formas },
              {
                columns: [
                  { text: formatarDiaMes(dias[0]!.dia), fontSize: 5.8, color: FRACO },
                  { text: `${dias.length} dias com medida`, fontSize: 5.8, color: FRACO, alignment: 'center' },
                  { text: formatarDiaMes(ultimo.dia), fontSize: 5.8, color: FRACO, alignment: 'right' },
                ],
                margin: [0, 1, 0, 0],
              },
            ],
          },
          {
            stack: [
              { text: `${formatarDiaMes(ultimo.dia)}: média ${num(ultimo.media)}`, fontSize: 7, color: TINTA },
              { text: `de ${num(ultimo.menor)} a ${num(ultimo.maior)}`, fontSize: 6.4, color: FRACO },
            ],
          },
        ];
      });
      conteudo.push(...comTitulo(
        {
          text: 'Dia a dia — cada traço vai do menor ao maior valor medido no dia; o ponto é a média do dia. Faixa verde: faixa usual de adulto (glicemia: alvo de paciente internado).',
          fontSize: 6.8, color: FRACO, italics: true, margin: [0, 8, 0, 3],
        },
        {
          table: { widths: [96, L, '*'], body: [linhas[0]!] },
          layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingLeft: () => 0, paddingRight: () => 6, paddingTop: () => 4, paddingBottom: () => 4 },
        },
        linhas.slice(1).map((l) => ({
          unbreakable: true,
          table: { widths: [96, L, '*'], body: [l] },
          layout: { hLineWidth: (i: number) => (i === 0 ? 0.3 : 0), hLineColor: () => LINHA, vLineWidth: () => 0, paddingLeft: () => 0, paddingRight: () => 6, paddingTop: () => 4, paddingBottom: () => 4 },
        })),
      ));
    }
  }

  // --------------------------------------------------------- laboratório
  if (dados.laboratorio.length > 0) {
    conteudo.push(titulo(++n, 'Laboratório',
      dados.gerado.laboratorioDesde ? `desde a última coleta, de ${formatarData(dados.gerado.laboratorioDesde)}` : undefined));
    // Uma linha que separa o que está fora da faixa do que está dentro.
    //
    // A ordenação já punha os alterados primeiro, mas nada dizia isso — e o
    // dono abriu justamente a página em que TODOS estavam normais, viu tudo
    // preto e concluiu que o PDF tinha perdido a cor. A tabela não pode
    // depender de quem lê perceber uma ordenação sozinho.
    const primeiroDentro = dados.laboratorio.findIndex(
      (l) => l.situacao !== 'alto' && l.situacao !== 'baixo',
    );
    const linhasDeLab: unknown[][] = [];
    dados.laboratorio.forEach((l, i) => {
      if (i === primeiroDentro && primeiroDentro > 0) {
        linhasDeLab.push([
          {
            text: 'daqui em diante, dentro da faixa usual',
            colSpan: 6, fontSize: 6.6, color: FRACO, italics: true,
            alignment: 'center', margin: [0, 2, 0, 2],
          }, {}, {}, {}, {}, {},
        ]);
      }
      linhasDeLab.push(linhaDeLab(l));
    });

    conteudo.push(tabela(
      ['*', 62, 46, 52, 56, 48],
      ['Exame', 'Último', 'Anterior', 'Tendência', 'Referência', 'Coletado'],
      linhasDeLab,
    ));
    conteudo.push({
      text: 'Valores fora da faixa usual de adulto vêm marcados com ALTO ou BAIXO ao lado do número — '
        + 'e também em cor, quando o documento é lido ou impresso colorido. É sinalização, não interpretação: '
        + 'quem interpreta é a equipe assistente.',
      fontSize: 7, color: FRACO, margin: [0, 5, 0, 0], italics: true,
    });

    // TODOS os alterados ganham explicação — não mais os oito primeiros.
    //
    // O corte em oito deixou de fora justamente o NT-proBNP, que estava 42
    // vezes acima do limite. Um relatório que explica o cálcio levemente baixo
    // e cala sobre o valor mais distante da página não está economizando
    // espaço: está escondendo o principal.
    const explicados = dados.laboratorio.filter((l) => l.explicacao && l.situacao !== 'dentro');
    if (paraFamilia && explicados.length > 0) {
      conteudo.push({
        margin: [0, 8, 0, 0],
        columns: [0, 1].map((coluna) => ({
          width: '*',
          margin: [0, 0, coluna === 0 ? 12 : 0, 0] as [number, number, number, number],
          stack: explicados
            .filter((_, i) => i % 2 === coluna)
            .map((l, i) => ({
              unbreakable: true,
              margin: [0, i === 0 ? 0 : 7, 0, 0] as [number, number, number, number],
              table: {
                widths: ['*'],
                body: [[{
                  margin: [7, 0, 0, 0],
                  stack: [
                    {
                      text: [
                        { text: l.rotulo, fontSize: 8, bold: true, color: TINTA },
                        // A distância da faixa, por extenso: "42 vezes acima do
                        // limite" é informação; "5.232, referência até 125"
                        // ainda exige uma conta de cabeça.
                        ...(l.vezesForaDaFaixa && l.vezesForaDaFaixa >= 1.5
                          ? [{
                              text: `   ${formatarNumero(l.vezesForaDaFaixa, 1)}× ${l.situacao === 'alto' ? 'acima do limite' : 'abaixo do mínimo'}`,
                              fontSize: 6.4, bold: true, color: corDaSituacao(l.situacao), characterSpacing: 0.3,
                            }]
                          : []),
                      ],
                    },
                    { text: l.explicacao!, fontSize: 7.8, color: MEIO, lineHeight: 1.3, margin: [0, 1.5, 0, 0] },
                    ...(l.pergunta
                      ? [{
                          text: [{ text: 'Para perguntar: ', bold: true, color: TINTA }, { text: l.pergunta, color: MEIO }],
                          fontSize: 7.6, lineHeight: 1.3, margin: [0, 2.5, 0, 0],
                        }]
                      : []),
                  ],
                }]],
              },
              layout: {
                hLineWidth: () => 0, vLineWidth: (c: number) => (c === 0 ? 1.4 : 0),
                vLineColor: () => LINHA,
                paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
              },
            })),
        })),
      });
    }
  }

  // ------------------------------------------------------------ culturas
  if (dados.culturas.length > 0) {
    const itensDeCultura = dados.culturas.map((c, i) => ({
        unbreakable: true,
        margin: [0, i === 0 ? 4 : 7, 0, 0] as [number, number, number, number],
        table: {
          widths: ['*'],
          body: [[{
            margin: [8, 0, 0, 0],
            stack: [
              {
                text: [
                  { text: c.material, bold: true, fontSize: 9, color: TINTA },
                  { text: `   colhida em ${formatarData(c.coletadoEm)}`, fontSize: 7.5, color: FRACO },
                  ...(c.deOutroSetor ? [{ text: '   OUTRO SETOR', fontSize: 5.8, bold: true, color: FRACO, characterSpacing: 0.6 }] : []),
                  ...(c.aguardando ? [{ text: '   AGUARDANDO', fontSize: 5.8, bold: true, color: ATENCAO, characterSpacing: 0.6 }] : []),
                ],
              },
                ...(c.resultado ? [{ text: c.resultado, fontSize: 8.2, color: MEIO, margin: [0, 2, 0, 0] as [number, number, number, number], lineHeight: 1.25 }] : []),
              ...(c.sensibilidade ? [{ text: `Sensível a: ${c.sensibilidade}`, fontSize: 7.6, color: FRACO, margin: [0, 1.5, 0, 0] as [number, number, number, number] }] : []),
              ...(paraFamilia && c.explicacao
                ? [{ text: c.explicacao, fontSize: 7.8, color: MEIO, italics: true, lineHeight: 1.3, margin: [0, 3, 0, 0] as [number, number, number, number] }]
                : []),
            ],
          }]],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: (col: number) => (col === 0 ? 1.6 : 0),
          vLineColor: () => (c.aguardando ? ATENCAO : LINHA),
          paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
        },
      }));
    conteudo.push(...comTitulo(
      titulo(++n, 'Culturas e antibiograma'),
      itensDeCultura[0],
      itensDeCultura.slice(1),
    ));
  }

  if (dados.culturas.some((c) => c.deOutroSetor) && dados.gerado.legendaDeSetor) {
    conteudo.push({
      text: dados.gerado.legendaDeSetor, fontSize: 7, color: FRACO,
      italics: true, margin: [0, 6, 0, 0],
    });
  }

  // ------------------------------------------------ imagem e procedimentos
  if (dados.procedimentos.length > 0) {
    const itensDeProcedimento = dados.procedimentos.map((x, i) => ({
        unbreakable: true,
        margin: [0, i === 0 ? 4 : 7, 0, 0] as [number, number, number, number],
        table: {
          widths: ['*'],
          body: [[{
            margin: [8, 0, 0, 0],
            stack: [
              {
                text: [
                  { text: x.nome, bold: true, fontSize: 9, color: TINTA },
                  { text: `   pedido em ${formatarData(x.solicitadoEm)}`, fontSize: 7.5, color: FRACO },
                  ...(x.realizadoEm ? [{ text: ` · realizado em ${formatarData(x.realizadoEm)}`, fontSize: 7.5, color: FRACO }] : []),
                  ...(x.deOutroSetor ? [{ text: '   OUTRO SETOR', fontSize: 5.8, bold: true, color: FRACO, characterSpacing: 0.6 }] : []),
                  ...(x.semLaudo ? [{ text: '   SEM LAUDO', fontSize: 5.8, bold: true, color: ATENCAO, characterSpacing: 0.6 }] : []),
                  ...(x.pendente ? [{ text: `   ${x.situacao.toUpperCase()} HÁ ${x.diasDesdeOPedido} ${x.diasDesdeOPedido === 1 ? 'DIA' : 'DIAS'}`, fontSize: 5.8, bold: true, color: ATENCAO, characterSpacing: 0.6 }] : []),
                ],
              },
              ...(x.resultado ? [{ text: x.resultado, fontSize: 8.2, color: MEIO, margin: [0, 2.5, 0, 0] as [number, number, number, number], lineHeight: 1.3 }] : []),
              ...(x.observacao ? [{ text: x.observacao, fontSize: 7.6, color: FRACO, margin: [0, 1.5, 0, 0] as [number, number, number, number], lineHeight: 1.25 }] : []),
              ...(paraFamilia && x.explicacao
                ? [{ text: x.explicacao, fontSize: 7.8, color: MEIO, italics: true, lineHeight: 1.3, margin: [0, 3, 0, 0] as [number, number, number, number] }]
                : []),
            ],
          }]],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: (col: number) => (col === 0 ? 1.6 : 0),
          vLineColor: () => (x.pendente ? ATENCAO : LINHA),
          paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
        },
      }));
    conteudo.push(...comTitulo(
      titulo(++n, 'Exames de imagem e procedimentos'),
      itensDeProcedimento[0],
      itensDeProcedimento.slice(1),
    ));
  }

  // ---------------------------------------------------------- medicações
  if (dados.medicacoes.length > 0) {
    conteudo.push(titulo(++n, 'Medicações'));
    conteudo.push(tabela(
      ['*', 104, 52, 52, 30],
      ['Medicação', 'Dose e via', 'Início', 'Fim', 'Dias'],
      dados.medicacoes.map((m) => [
        {
          stack: [
            {
              // Mesma regra do dispositivo: toda linha diz o próprio estado.
              text: [
                { text: m.nome, bold: true, color: m.emUso ? TINTA : MEIO },
                m.emUso
                  ? { text: '   EM USO', fontSize: 5.8, color: OK, bold: true, characterSpacing: 0.6 }
                  : { text: '   ENCERRADA', fontSize: 5.8, color: ALTO, bold: true, characterSpacing: 0.6 },
              ],
            },
            ...(m.diaDoCurso && m.diasDoCurso
              ? [{
                  text: `dia ${m.diaDoCurso} de ${m.diasDoCurso}`
                    + (m.faltamDias !== null && m.faltamDias >= 0 && m.previsaoFimEm
                      ? ` · termina em ${formatarData(m.previsaoFimEm)}`
                        + (m.faltamDias === 0 ? ' (hoje)' : ` (faltam ${m.faltamDias} ${m.faltamDias === 1 ? 'dia' : 'dias'})`)
                      : ''),
                  fontSize: 6.4, color: ATENCAO, margin: [0, 1, 0, 0] as [number, number, number, number],
                }]
              : []),
            ...(m.precisaConfirmar
              ? [{
                  text: m.precisaConfirmar, fontSize: 6.4, color: ATENCAO, italics: true,
                  margin: [0, 1, 0, 0] as [number, number, number, number],
                }]
              : []),
          ],
        },
        { text: [m.dose, m.via].filter(Boolean).join(' · ') || '—', color: FRACO },
        { text: formatarData(m.inicioEm), color: MEIO },
        { text: m.fimEm ? formatarData(m.fimEm) : m.previsaoFimEm ? formatarData(m.previsaoFimEm) : '—', color: FRACO, italics: !m.fimEm && Boolean(m.previsaoFimEm) },
        { text: String(m.dias), color: MEIO },
      ]),
    ));
  }

  // A explicação de cada remédio em uso, com o aviso de exame de controle.
  const remediosExplicados = dados.medicacoes.filter((m) => m.emUso && m.explicacao);
  if (paraFamilia && remediosExplicados.length > 0) {
    conteudo.push({
      margin: [0, 7, 0, 0],
      stack: remediosExplicados.map((m, i) => ({
        unbreakable: true,
        margin: [0, i === 0 ? 0 : 5, 0, 0] as [number, number, number, number],
        table: {
          widths: ['*'],
          body: [[{
            margin: [7, 0, 0, 0],
            stack: [
              { text: m.nome, fontSize: 8, bold: true, color: TINTA },
              { text: m.explicacao!.texto, fontSize: 7.8, color: MEIO, lineHeight: 1.3, margin: [0, 1.5, 0, 0] },
              ...(m.explicacao!.aviso
                ? [{ text: m.explicacao!.aviso, fontSize: 7.8, color: ATENCAO, bold: true, lineHeight: 1.3, margin: [0, 2.5, 0, 0] }]
                : []),
            ],
          }]],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: (c: number) => (c === 0 ? 1.4 : 0),
          vLineColor: () => (m.explicacao!.aviso ? ATENCAO : LINHA),
          paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
        },
      })),
    });
  }

  // -------------------------------------------------------- dispositivos
  if (dados.dispositivos.length > 0) {
    conteudo.push(titulo(++n, 'Dispositivos'));
    conteudo.push(tabela(
      ['*', 118, 52, 52, 30],
      ['Dispositivo', 'Local', 'Inserido', 'Retirado', 'Dias'],
      dados.dispositivos.map((d) => [
        {
          // Ausência de etiqueta não é informação: quem lia a linha sem
          // "instalado" não sabia se o dispositivo saiu ou se faltou o dado.
          // Cada linha diz em que estado está, sempre.
          text: [
            { text: d.nome, bold: true, color: d.instalado ? TINTA : MEIO },
            d.instalado
              ? { text: '   INSTALADO', fontSize: 5.8, color: OK, bold: true, characterSpacing: 0.6 }
              : { text: '   RETIRADO', fontSize: 5.8, color: ALTO, bold: true, characterSpacing: 0.6 },
          ],
        },
        { text: d.local ?? '—', color: FRACO },
        { text: formatarData(d.inseridoEm), color: MEIO },
        { text: d.retiradoEm ? formatarData(d.retiradoEm) : '—', color: d.retiradoEm ? MEIO : FRACO },
        { text: String(d.dias), color: d.instalado && d.dias >= 14 ? ALTO : MEIO },
      ]),
    ));
  }

  // ------------------------------------------------- o que foi aplicado
  //
  // Responde no papel a pergunta que o médico faz de pé, ao lado do leito:
  // "que horas foi o último antibiótico?". Sai da conferência pela foto, e traz
  // o nome como estava NO RÓTULO — o que subiu na bomba, não o que se esperava.
  if (dados.aplicacoes.length > 0) {
    conteudo.push(titulo(++n, 'Medicação aplicada e conferida', dados.gerado.periodoRotulo));
    conteudo.push({
      margin: [0, 4, 0, 0],
      table: {
        headerRows: 1, dontBreakRows: true, widths: [72, '*', 92],
        body: [
          ['Quando', 'Medicação', 'Conferência'].map((t) => ({
            text: t, fontSize: 6.6, bold: true, color: FRACO,
            characterSpacing: 0.6, margin: [0, 0, 0, 3] as [number, number, number, number],
          })),
          ...dados.aplicacoes.map((a) => [
            { text: formatarDataHora(a.ocorreuEm), fontSize: 7.6, color: TINTA, bold: true },
            {
              stack: [
                { text: a.nome + (a.dose ? ` · ${a.dose}` : ''), fontSize: 8.2, color: MEIO },
                ...(a.observacao ? [{ text: a.observacao, fontSize: 6.8, color: FRACO }] : []),
              ],
            },
            {
              // Em preto e branco a cor não diz nada: o aviso vem por escrito.
              text: a.veredito === 'confere'
                ? `conferido · ${a.conferidoPor}`
                : a.veredito === 'feito'
                  // Quem estava no quarto viu ser dado, sem foto do rótulo.
                  ? `visto ser dado · ${a.conferidoPor}`
                  : a.veredito === 'pare'
                    ? `APLICADO APESAR DO ALERTA · ${a.conferidoPor}`
                    : `sem confirmação completa · ${a.conferidoPor}`,
              fontSize: 6.8, color: a.veredito === 'confere' || a.veredito === 'feito' ? FRACO : TINTA,
              bold: a.veredito !== 'confere' && a.veredito !== 'feito',
            },
          ]),
        ],
      },
      layout: {
        hLineWidth: (i: number, node: { table: { body: unknown[] } }) => (i === 0 || i === node.table.body.length ? 0 : 0.4),
        vLineWidth: () => 0, hLineColor: () => LINHA,
        paddingLeft: () => 0, paddingRight: () => 8, paddingTop: () => 5, paddingBottom: () => 5,
      },
    });
  }

  // ------------------------------------------------------------ evolução
  // ------------------------------------------------------ como ela está
  if (dados.evolucao.observacoes.length > 0) {
    const blocos: unknown[] = [];
    if (dados.evolucao.tendencia) {
      blocos.push({
        text: dados.evolucao.tendencia, fontSize: 8.4, color: TINTA, bold: true,
        margin: [0, 4, 0, 4] as [number, number, number, number],
      });
    }
    blocos.push(tabela(
      [36, 42, 34, 54, 96, '*'],
      ['Dia', ...PERGUNTAS_DO_DIA.map((p) => p.curto), 'Sono', 'O que chamou atenção'],
      dados.evolucao.observacoes.map((o) => [
        { text: formatarDiaMes(o.dia), bold: true },
        // Palavra, não símbolo: a Roboto embutida já engoliu glifo aqui antes.
        ...PERGUNTAS_DO_DIA.map((p) => ({
          text: ROTULO_RESPOSTA[o[p.chave]],
          color: o[p.chave] === 'sim' ? OK : o[p.chave] === 'nao' ? ALTO : ATENCAO,
          bold: true, fontSize: 7,
        })),
        { text: sonoEmTexto(o) ?? '—', color: FRACO, fontSize: 7 },
        { text: o.observacao ?? '', color: MEIO, fontSize: 7.4 },
      ]),
    ));
    blocos.push({
      text: 'Observação da família, feita todo dia na mesma hora, com as mesmas três perguntas: '
        + `${PERGUNTAS_DO_DIA.map((p) => p.pergunta.toLowerCase()).join(' · ')} `
        + 'Não é escala clínica: um dia não diz nada, e é a sequência que mostra se está mudando.',
      fontSize: 6.6, color: FRACO, lineHeight: 1.3,
      margin: [0, 4, 0, 0] as [number, number, number, number],
    });
    conteudo.push(...comTitulo(
      titulo(++n, 'Como ela está, dia a dia', `últimos ${dados.evolucao.dias} dias`),
      blocos[0], blocos.slice(1),
    ));
  }

  // ------------------------------------------------- intercorrências
  // Depois dos dados, não antes (pedido do Ilton, 25/09): aberta logo abaixo do
  // resumo, a troca de medicação de 18/09 ocupava a página 2 inteira e quem lia
  // chegava aos exames na página 3. Continua no documento, no período dela.
  if (dados.intercorrencias.length > 0) {
    const itens = dados.intercorrencias.map((i, k) => {
      const cor = i.gravidade === 'grave' ? ALTO : i.gravidade === 'moderada' ? ATENCAO : FRACO;
      return {
        unbreakable: true,
        margin: [0, k === 0 ? 4 : 7, 0, 0] as [number, number, number, number],
        table: {
          widths: ['*'],
          body: [[{
            margin: [9, 0, 0, 0],
            stack: [
              {
                text: [
                  { text: i.titulo, bold: true, fontSize: 9.5, color: TINTA },
                  { text: `   ${formatarDataHora(i.ocorreuEm)}`, fontSize: 7.5, color: FRACO },
                  { text: `   ${i.gravidade.toUpperCase()}`, fontSize: 6, bold: true, color: cor, characterSpacing: 0.7 },
                  ...(i.desfecho === 'evitado'
                    ? [{ text: '   INTERROMPIDO A TEMPO', fontSize: 6, bold: true, color: OK, characterSpacing: 0.7 }]
                    : i.desfecho === 'aconteceu'
                      ? [{ text: '   CHEGOU A ACONTECER', fontSize: 6, bold: true, color: ALTO, characterSpacing: 0.7 }]
                      : []),
                ],
              },
              { text: i.oQueAconteceu, fontSize: 8.2, color: MEIO, lineHeight: 1.3, margin: [0, 3, 0, 0] },
              ...(i.oQueEraEsperado
                ? [{ text: [{ text: 'O que era para ser: ', bold: true, color: TINTA }, { text: i.oQueEraEsperado, color: MEIO }], fontSize: 8, lineHeight: 1.3, margin: [0, 2.5, 0, 0] }]
                : []),
              ...(i.risco
                ? [{ text: [{ text: 'Risco do que foi administrado: ', bold: true, color: cor }, { text: i.risco, color: MEIO }], fontSize: 8, lineHeight: 1.3, margin: [0, 2.5, 0, 0] }]
                : []),
              ...(i.oQueFoiFeito
                ? [{ text: [{ text: 'O que foi feito: ', bold: true, color: TINTA }, { text: i.oQueFoiFeito, color: MEIO }], fontSize: 8, lineHeight: 1.3, margin: [0, 2.5, 0, 0] }]
                : []),
              {
                text: [i.quemDetectou ? `Detectado por ${i.quemDetectou}.` : '', i.registradoPor ? ` Registrado por ${i.registradoPor}.` : ''].join(''),
                fontSize: 7, color: FRACO, italics: true, margin: [0, 3, 0, 0],
              },
            ],
          }]],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: (c: number) => (c === 0 ? 2.2 : 0),
          vLineColor: () => cor,
          paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
        },
      };
    });
    conteudo.push(...comTitulo(
      titulo(++n, 'Intercorrências', dados.gerado.periodoRotulo),
      itens[0],
      itens.slice(1),
    ));
  }

  // Os relatos só na passagem de turno: nos outros períodos eles têm documento
  // próprio, para não empurrar os exames para longe de quem lê.
  if (periodo === 'turno' && dados.anotacoes.length > 0) {
    conteudo.push(titulo(++n, 'Relatos do turno', dados.gerado.periodoRotulo));
    conteudo.push({
      margin: [0, 4, 0, 0],
      table: {
        headerRows: 0, dontBreakRows: true, widths: [72, '*'],
        body: dados.anotacoes.map((a) => [
          {
            stack: [
              { text: formatarDataHora(a.ocorreuEm), fontSize: 7.6, color: TINTA, bold: true },
              { text: a.autor, fontSize: 6.6, color: FRACO },
            ],
          },
          notaNoPdf(a.texto, 8.2, { texto: TINTA, medio: MEIO, linha: LINHA, alerta: ALTO }),
        ]),
      },
      layout: {
        hLineWidth: (i: number, node: { table: { body: unknown[] } }) => (i === 0 || i === node.table.body.length ? 0 : 0.4),
        vLineWidth: () => 0, hLineColor: () => LINHA,
        paddingLeft: () => 0, paddingRight: () => 8, paddingTop: () => 5, paddingBottom: () => 5,
      },
    });
  }

  // ---------------------------------------------- perguntas para a visita
  if (paraFamilia && dados.perguntas.length > 0) {
    const caixaDePerguntas = {
      margin: [0, 2, 0, 0],
      table: {
        widths: ['*'],
        body: [[{
          margin: [10, 8, 10, 8],
          stack: dados.perguntas.map((q, i) => ({
            margin: [0, i === 0 ? 0 : 6, 0, 0] as [number, number, number, number],
            stack: [
              { text: q.sobre.toUpperCase(), fontSize: 6.2, bold: true, color: OK, characterSpacing: 0.7 },
              {
                text: [{ text: `${i + 1}. `, color: FRACO }, { text: q.texto, color: TINTA }],
                fontSize: 9, lineHeight: 1.25, margin: [0, 1.5, 0, 0],
              },
            ],
          })),
        }]],
      },
      layout: {
        hLineWidth: () => 0.7, vLineWidth: () => 0.7,
        hLineColor: () => OK, vLineColor: () => OK,
        fillColor: () => FUNDO_CAIXA,
        paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
      },
    };
    conteudo.push(...comTitulo(
      titulo(++n, 'Para perguntar na visita', p.quarto ? `à equipe do quarto ${p.quarto}` : undefined),
      caixaDePerguntas,
    ));
  }

  // ------------------------------------------------- o que já foi respondido
  //
  // A resposta vale mais que a pergunta. "A heparina é para prevenir trombose,
  // porque ela está muito deitada" some no caminho do corredor até em casa — e
  // é o que a próxima pessoa do quarto precisa para não perguntar de novo.
  if (paraFamilia && dados.respondidas.length > 0) {
    conteudo.push(...comTitulo(
      titulo(++n, 'Já respondido pela equipe', dados.gerado.periodoRotulo),
      {
        margin: [0, 4, 0, 0],
        table: {
          headerRows: 0, dontBreakRows: true, widths: ['*'],
          body: dados.respondidas.map((r) => [{
            stack: [
              { text: r.pergunta, fontSize: 7.4, color: FRACO, lineHeight: 1.25 },
              ...(r.resposta
                ? [{ text: r.resposta, fontSize: 9, color: TINTA, lineHeight: 1.3, margin: [0, 2, 0, 0] as [number, number, number, number] }]
                : [{ text: 'Respondida na visita; a resposta não foi anotada.', fontSize: 8, color: FRACO, italics: true, margin: [0, 2, 0, 0] as [number, number, number, number] }]),
              {
                text: `${r.quemRespondeu ? `${r.quemRespondeu} · ` : ''}anotado por ${r.registradaPor} em ${formatarDataHora(r.respondidaEm)}`,
                fontSize: 6.4, color: FRACO, margin: [0, 2, 0, 0] as [number, number, number, number],
              },
            ],
          }]),
        },
        layout: {
          hLineWidth: (i: number, node: { table: { body: unknown[] } }) => (i === 0 || i === node.table.body.length ? 0 : 0.4),
          vLineWidth: () => 0, hLineColor: () => LINHA,
          paddingLeft: () => 0, paddingRight: () => 8, paddingTop: () => 5, paddingBottom: () => 5,
        },
      },
    ));
  }

  if (paraFamilia && dados.deOutrosSetores.length > 0) {
    conteudo.push(...comTitulo(titulo(++n, 'Pedido em outro setor'), {
      text: `Isto foi pedido antes da transferência${p.quarto ? ` para o quarto ${p.quarto}` : ''}, por outra equipe. A equipe atual não acompanhou esses pedidos e não tem como respondê-los — o resultado consta no prontuário do hospital. Por isso não entrou na lista de perguntas: ${dados.deOutrosSetores.join('; ')}.`,
      fontSize: 8, color: MEIO, lineHeight: 1.3,
    }));
  }

  // ------------------------------------------------------------- plantão
  if (dados.plantoes.length > 0) {
    // Título e tabela num bloco só quando cabe numa página: no semanal de 25/09
    // o título ficou sozinho no pé da folha, só com o cabeçalho da tabela.
    const cabe = dados.plantoes.length <= 12;
    const tituloDoPlantao = titulo(++n, 'Equipe de plantão', dados.gerado.periodoRotulo);
    if (!cabe) conteudo.push(tituloDoPlantao);
    const tabelaDoPlantao = tabela(
      [52, 74, 74, '*'],
      ['Turno', 'De', 'Até', 'Equipe'],
      dados.plantoes.map((t) => [
        { text: t.turno, bold: true },
        { text: formatarDataHora(t.inicioEm), color: MEIO },
        { text: t.fimEm ? formatarDataHora(t.fimEm) : 'em curso', color: t.fimEm ? MEIO : OK },
        { text: t.equipe.join(' · '), color: MEIO },
      ]),
    );
    conteudo.push(...(cabe ? comTitulo(tituloDoPlantao, tabelaDoPlantao) : [tabelaDoPlantao]));
  }

  // --------------------------------------------------------------- fecho
  conteudo.push({
    margin: [0, 16, 0, 0],
    table: {
      widths: ['*'],
      body: [[{
        margin: [0, 6, 0, 0],
        stack: [
          ...(dados.gerado.aviso ? [{ text: dados.gerado.aviso, fontSize: 7.2, color: MEIO, lineHeight: 1.3 }] : []),
          {
            text: `Documento gerado pelo saudev1 em ${formatarDataHora(dados.gerado.em)}, no horário de Brasília, a partir dos registros feitos pela família. Período considerado: ${dados.gerado.periodoRotulo}.`,
            fontSize: 7, color: FRACO, margin: [0, 4, 0, 0], lineHeight: 1.3,
          },
        ],
      }]],
    },
    layout: {
      hLineWidth: (i: number) => (i === 0 ? 0.8 : 0),
      vLineWidth: () => 0, hLineColor: () => '#a1a1aa',
      paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
    },
  });

  // O timbre e o rodapé se repetem em cada folha: da página 2 em diante, um
  // documento sem identificação é um monte de tabela solta.
  const identificacao = [
    nomeNoTopo,
    ...(!paraFamilia && p.registro ? [`prontuário ${p.registro}`] : []),
    ...(p.quarto ? [`quarto ${p.quarto}`] : []),
  ].join(' · ');

  const MARGEM = 42;

  return {
    pageSize: 'A4',
    pageMargins: [MARGEM, 52, MARGEM, 46],
    info: {
      title: `Relatório ${ROTULO_PERIODO[periodo].toLowerCase()} — ${p.apelido}`,
      author: 'saudev1 · registro de acompanhamento da família',
      subject: `Acompanhamento de internação — ${dados.gerado.periodoRotulo}`,
    },
    defaultStyle: { font: 'Roboto', fontSize: 8.4, color: TINTA, lineHeight: 1.15 },
    header: (paginaAtual: number) => (paginaAtual === 1 ? undefined : {
      margin: [MARGEM, 22, MARGEM, 0],
      table: {
        widths: ['*', 'auto'],
        body: [[
          { text: identificacao.toUpperCase(), fontSize: 6.4, bold: true, color: FRACO, characterSpacing: 0.8 },
          { text: `RELATÓRIO ${ROTULO_PERIODO[periodo].toUpperCase()} · ${formatarData(new Date(dados.gerado.em))}`, fontSize: 6.4, bold: true, color: FRACO, characterSpacing: 0.8, alignment: 'right' },
        ]],
      },
      layout: {
        hLineWidth: (i: number) => (i === 1 ? 0.5 : 0),
        vLineWidth: () => 0, hLineColor: () => LINHA,
        paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 5,
      },
    }),
    footer: (paginaAtual: number, total: number) => ({
      margin: [MARGEM, 10, MARGEM, 0],
      table: {
        widths: ['*', 'auto'],
        body: [[
          { text: 'Registro de acompanhamento feito pela família · não é o prontuário do hospital', fontSize: 6.4, color: FRACO },
          { text: `saudev1   ·   ${paginaAtual}/${total}`, fontSize: 6.4, color: FRACO, bold: true, alignment: 'right' },
        ]],
      },
      layout: {
        hLineWidth: (i: number) => (i === 0 ? 0.5 : 0),
        vLineWidth: () => 0, hLineColor: () => LINHA,
        paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 5, paddingBottom: () => 0,
      },
    }),
    content: conteudo,
  };
}

/** Monta o documento e baixa. Um clique, um arquivo — sem diálogo de impressão. */
export async function gerarPdf(
  dados: RelatorioDTO,
  periodo: Periodo,
  versao: Versao,
): Promise<void> {
  await baixarPdfDe(
    montarDocumento(dados, periodo, versao),
    nomeDoArquivo(dados.paciente.apelido, periodo, versao, dados.gerado.em),
  );
}

/**
 * Carrega o pdfmake e as fontes, monta e baixa. É o único lugar que sabe como
 * o pdfmake se inicializa — o relatório de acompanhamento e o de exames de
 * imagem passam os dois por aqui.
 */
export async function baixarPdfDe(definicao: unknown, nome: string): Promise<void> {
  // A API mudou na versão 0.3 do pdfmake: não existe mais `pdfMake.vfs = ...`.
  // Agora as fontes chegam num "container" com os arquivos em base64, que são
  // escritos no sistema de arquivos virtual e só então declarados.
  const [modulo, container] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/fonts/Roboto.js'),
  ]);
  const pdfMake = ((modulo as { default?: unknown }).default ?? modulo) as {
    virtualfs: { writeFileSync: (nome: string, dados: unknown) => void };
    addFonts: (fontes: unknown) => void;
    createPdf: (d: unknown) => { download: (nome: string) => void };
  };
  const roboto = ((container as { default?: unknown }).default ?? container) as {
    vfs: Record<string, { data: string } | string>;
    fonts: unknown;
  };
  for (const [nome, arquivo] of Object.entries(roboto.vfs)) {
    pdfMake.virtualfs.writeFileSync(nome, deBase64(typeof arquivo === 'string' ? arquivo : arquivo.data));
  }
  pdfMake.addFonts(roboto.fonts);

  pdfMake.createPdf(definicao).download(nome);
}

/**
 * O .ttf vem em base64 e precisa chegar como bytes.
 *
 * Entregar a string crua faz o pdfmake morrer com "Unknown font format" — e
 * morre só no navegador, o que é a parte perigosa: em Node o mesmo código
 * funcionava porque lá a conversão já era feita com `Buffer`. Um teste que
 * rodava fora do navegador nunca teria visto isto.
 */
function deBase64(b64: string): Uint8Array {
  const bruto = atob(b64);
  const bytes = new Uint8Array(bruto.length);
  for (let i = 0; i < bruto.length; i++) bytes[i] = bruto.charCodeAt(i);
  return bytes;
}

/** "relatorio-semanal-dona-maria-17-09-2026.pdf" — reconhecível no WhatsApp. */
function nomeDoArquivo(apelido: string, periodo: Periodo, versao: Versao, emISO: string): string {
  const limpo = apelido.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `relatorio-${periodo}-${versao}-${limpo}-${formatarData(new Date(emISO)).replace(/\//g, '-')}.pdf`;
}
